import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { MessageSquare, ArrowLeft } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

import { cn } from '@/lib/cn';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/EmptyState';
import { MessageThread } from '@/features/Messages/MessageThread';
import { OrgAvatar } from '@/features/OrgLayout/primitives/OrgAvatar';
import { OrgCard } from '@/features/OrgLayout/primitives/OrgCard';
import { useOrgInbox, useOrgThread, useSendMessage } from '@/lib/hooks/useMessages';
import type { OrgThreadPreview } from '@/lib/api/messages';

export const Route = createFileRoute('/org/messages')({
  component: OrgMessagesPage,
});

type StatusFilter = 'needs_reply' | 'all' | 'answered';

const needsReply = (t: OrgThreadPreview) => t.last_sender_id === t.student_id;

function relativeTime(raw: string): string {
  if (!raw) return '';
  const d = new Date(raw.replace(' ', 'T'));
  if (Number.isNaN(d.getTime())) return '';
  return formatDistanceToNow(d, { addSuffix: true });
}

function OrgMessagesPage() {
  const { data: threads, isLoading } = useOrgInbox();
  const [status, setStatus] = useState<StatusFilter>('needs_reply');
  const [batchName, setBatchName] = useState<string>('all');
  const [selId, setSelId] = useState<string | null>(null);

  const all = useMemo(() => threads ?? [], [threads]);
  const needsReplyCount = all.filter(needsReply).length;

  const batches = useMemo(() => {
    const set = new Set<string>();
    all.forEach((t) => set.add(t.batch_name));
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [all]);

  const visible = useMemo(() => {
    return all
      .filter((t) => (batchName === 'all' ? true : t.batch_name === batchName))
      .filter((t) => {
        if (status === 'needs_reply') return needsReply(t);
        if (status === 'answered') return !needsReply(t);
        return true;
      })
      .sort((a, b) => {
        // Needs-reply first, then most-recent activity.
        const aNeeds = needsReply(a) ? 1 : 0;
        const bNeeds = needsReply(b) ? 1 : 0;
        if (aNeeds !== bNeeds) return bNeeds - aNeeds;
        return (b.last_at ?? '').localeCompare(a.last_at ?? '');
      });
  }, [all, batchName, status]);

  const selected = visible.find((t) => t.student_id === selId) ?? null;

  const tabs: { key: StatusFilter; label: string; count?: number }[] = [
    { key: 'needs_reply', label: 'Needs reply', count: needsReplyCount },
    { key: 'all', label: 'All', count: all.length },
    { key: 'answered', label: 'Answered' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-[36px] font-bold leading-tight text-[#111827]">Messages</h1>
          <p className="pt-1 text-base text-[#6b7280]">Conversations with students across your batches.</p>
        </div>
        {batches.length > 1 && (
          <select
            value={batchName}
            onChange={(e) => setBatchName(e.target.value)}
            className="h-[42px] rounded-[14px] border border-[#ececec] bg-white px-[17px] text-sm font-medium text-[#6b7280]"
            aria-label="Filter by batch"
          >
            <option value="all">All batches</option>
            {batches.map((b) => (
              <option key={b} value={b}>{b}</option>
            ))}
          </select>
        )}
      </div>

      <div className="flex gap-1 border-b border-[#ececec]">
        {tabs.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setStatus(t.key)}
            className={cn(
              'inline-flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium transition-colors',
              status === t.key
                ? 'border-[#ff5a1f] text-[#111827]'
                : 'border-transparent text-[#6b7280] hover:text-[#111827]',
            )}
          >
            {t.label}
            {t.count != null && t.count > 0 && (
              <span className={cn(
                'rounded-full px-1.5 py-0.5 text-[11px] font-semibold',
                t.key === 'needs_reply' ? 'bg-[#fff4f0] text-[#ff5a1f]' : 'bg-[#f3f4f6] text-[#6b7280]',
              )}>{t.count}</span>
            )}
          </button>
        ))}
      </div>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,380px)_1fr]">
        {/* Thread list */}
        <div className={cn('min-w-0', selected && 'hidden lg:block')}>
          {isLoading ? (
            <div className="space-y-2">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-16 rounded-xl" />)}</div>
          ) : visible.length === 0 ? (
            <EmptyState
              icon={MessageSquare}
              title={status === 'needs_reply' ? 'All caught up' : 'No messages yet'}
              description={
                status === 'needs_reply'
                  ? 'No students are waiting on a reply right now.'
                  : 'When students message your team, their threads appear here.'
              }
            />
          ) : (
            <OrgCard className="divide-y divide-[#ececec] overflow-hidden">
              {visible.map((t) => {
                const waiting = needsReply(t);
                return (
                  <button
                    key={t.student_id}
                    type="button"
                    onClick={() => setSelId(t.student_id)}
                    className={cn(
                      'flex w-full items-start gap-3 px-3 py-3 text-left transition-colors',
                      t.student_id === selId ? 'bg-[#fafafa]' : 'hover:bg-[#fafafa]',
                    )}
                  >
                    <span className="mt-1.5 flex h-2 w-2 shrink-0 items-center justify-center">
                      {waiting && <span className="h-2 w-2 rounded-full bg-[#ff5a1f]" aria-label="Needs reply" />}
                    </span>
                    <OrgAvatar name={t.student_name} size={36} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <p className={cn('truncate text-sm', waiting ? 'font-semibold text-[#111827]' : 'font-medium text-[#111827]')}>
                          {t.student_name}
                        </p>
                        <span className="shrink-0 text-[11px] text-[#9ca3af]">{relativeTime(t.last_at)}</span>
                      </div>
                      <div className="mt-1 flex items-center gap-2">
                        <span className="truncate rounded-full bg-[#f3f4f6] px-2 py-0.5 text-[10px] font-medium text-[#6b7280]">
                          {t.batch_name}
                        </span>
                        <p className="truncate text-xs text-[#6b7280]">{t.last_body}</p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </OrgCard>
          )}
        </div>

        {/* Conversation */}
        <div className={cn('min-w-0', !selected && 'hidden lg:block')}>
          {selected ? (
            <ThreadPane thread={selected} onBack={() => setSelId(null)} />
          ) : (
            <div className="hidden h-full min-h-[420px] flex-col items-center justify-center rounded-[24px] border border-dashed border-[#ececec] text-center lg:flex">
              <MessageSquare className="h-8 w-8 text-[#9ca3af]" />
              <p className="mt-2 text-sm text-[#6b7280]">Select a conversation to read and reply.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ThreadPane({ thread, onBack }: { thread: OrgThreadPreview; onBack: () => void }) {
  const { data, isLoading } = useOrgThread(thread.student_id, true);
  const send = useSendMessage(['messages', 'org', thread.student_id]);
  return (
    <OrgCard className="p-4">
      <div className="flex items-start gap-3 border-b border-[#ececec] pb-3">
        <button
          type="button"
          onClick={onBack}
          className="mt-0.5 inline-flex rounded-lg border border-[#ececec] p-1.5 text-[#6b7280] lg:hidden"
          aria-label="Back to messages"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <OrgAvatar name={thread.student_name} size={40} />
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-[#111827]">{thread.student_name}</p>
          <p className="truncate text-xs text-[#6b7280]">{thread.batch_name}</p>
        </div>
      </div>
      <MessageThread
        messages={data?.messages ?? []}
        self={data?.self ?? ''}
        loading={isLoading}
        sending={send.isPending}
        onSend={(body) => send.mutate({ student_id: thread.student_id, body })}
        emptyText={`Start a conversation with ${thread.student_name}.`}
      />
    </OrgCard>
  );
}
